import styled from '@emotion/styled';
import { ClaimIdBadge } from '../../badge';
import { BaseButton, Variables } from '../../base';

interface INote {
    note?: boolean;
}

const noteHeader = `
    padding: 16px 24px;
    gap: 10px;
    background: linear-gradient(180deg, ${Variables.ThemeLight.NeutralMild2} 0%, ${Variables.ThemeLight.Background2} 100%);
`;

const commentHeader = `
    padding: 16px 24px 0px;
    gap: 10px;
    color: ${Variables.Colors.n85};
    background-color: ${Variables.ThemeLight.Background2};
`;

export const Header = styled.div<INote>`
    display: flex;
    flex-direction: column;
    ${(props: INote): string | undefined => (props.note ? noteHeader : commentHeader)}
`;

export const Row = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

export const SubjectRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

export const SubjectPills = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: none;
`;

export const AssigneeSlot = styled.div`
    margin-left: auto;
    flex: none;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
`;

export const CategoryPills = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex: none;
    flex-wrap: wrap;
    max-width: 280px;
`;

export const MessageGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const Subject = styled.div`
    font-weight: ${Variables.FontWeight.semibold};
    font-size: ${Variables.FontSizes.large};
    leading-trim: NONE;
    line-height: 100%;
    letter-spacing: 0%;
    color: ${Variables.ThemeLight.TextStrong};
    cursor: pointer;
    min-width: 0;
    flex: 1 1 auto;
`;

export const EditableSubject = styled.div`
    cursor: pointer;
    flex: 1;
    min-width: 0;
`;

export const DueDate = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex: none;
    font-size: 12px;
    font-weight: ${Variables.FontWeight.medium};
    color: ${Variables.ThemeLight.AttentionConstant6};
`;

export const ClaimButton = styled(ClaimIdBadge)`
    margin-right: 8px;
    margin-bottom: 8px;
    align-self: baseline;
`;

export const Body = styled.div`
    font-weight: ${Variables.FontWeight.regular};
    font-size: 14px;
    line-height: 1.45;
    letter-spacing: 0;
    color: ${Variables.ThemeLight.TextMedium};

    pre,
    code {
        white-space: pre-wrap;
        word-wrap: break-word;
        overflow-wrap: break-word;
    }
`;

export const Attachments = styled.div``;

export const LoadingText = styled.div`
    font-size: 14px;
    color: ${Variables.Colors.n50};
    font-style: italic;
    padding: 8px 0;
`;

export const MetaSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

export const ContentSections = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

/** Wrapper table: Claims and Codings as collapsible rows in one table. */
export const SectionsTable = styled.div`
    width: 100%;
    border: 1px solid ${Variables.ThemeLight.Border3};
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

export const SectionRow = styled.div`
    &:not(:last-child) {
        border-bottom: 1px solid ${Variables.ThemeLight.Border2};
    }
`;

// Codings table styles
export const CodingsTable = styled.table`
    width: 100%;
    background-color: ${Variables.Colors.white};
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 4px;
    overflow: hidden;
    border-spacing: 0px;

    thead th {
        border-bottom: 1px solid ${Variables.ThemeLight.Border2};
    }
    tbody tr:not(:last-child) td {
        border-bottom: 1px solid ${Variables.ThemeLight.Border2};
    }

    thead tr:first-of-type th:first-of-type {
        border-top-left-radius: 4px;
    }
    thead tr:first-of-type th:last-of-type {
        border-top-right-radius: 4px;
    }
    tbody tr:last-child td:first-of-type {
        border-bottom-left-radius: 4px;
    }
    tbody tr:last-child td:last-of-type {
        border-bottom-right-radius: 4px;
    }
`;

export const CodingsTableHead = styled.thead`
    background-color: ${Variables.ThemeLight.Background1};
`;

export const CodingsTableBody = styled.tbody`
    background-color: ${Variables.ThemeLight.Background2};
    overflow: visible;
`;

export const CodingsTableRow = styled.tr`
    &:not(:last-child) {
    }

    &:hover {
        background-color: ${Variables.Colors.n5};
    }
`;

export const CodingsTableHeaderCell = styled.th`
    text-align: left;
    padding: 12px;
    font-size: 14px;
    font-weight: ${Variables.FontWeight.medium};
    color: ${Variables.ThemeLight.TextMedium};
    letter-spacing: 0.5px;
`;

export const CodingsTableCell = styled.td`
    padding: 12px;
    font-size: 14px;
    color: ${Variables.ThemeLight.TextMedium};
    font-weight: ${Variables.FontWeight.regular};
    vertical-align: middle;
`;

export const NotesCell = styled(CodingsTableCell)`
    max-width: 500px;
    white-space: pre-wrap;
    word-break: break-word;
`;

export const InfoButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 8px;
    background-color: transparent;
    border: 1px solid ${Variables.Colors.b200};
    border-radius: 4px;
    color: ${Variables.Colors.b300};
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 12px;

    &:hover {
        background-color: ${Variables.Colors.b50};
        border-color: ${Variables.Colors.b300};
    }

    &:active {
        transform: scale(0.95);
    }
`;

export const EmptyMessage = styled.div`
    text-align: center;
    padding: 32px;
    color: ${Variables.Colors.n50};
    font-size: 14px;
`;

export const CustomAIButton = styled(BaseButton)`
    padding: 0;
    height: auto;
    font-size: 13px;
    background-color: ${Variables.ThemeLight.PrimaryConstant6};
    color: ${Variables.Colors.white};
    padding: 1px 6px;
    & > img {
        display: inline-block;
    }

    &:hover {
        background-color: ${Variables.ThemeLight.PrimaryInverted5};
    }
    &:active {
        background-color: ${Variables.ThemeLight.PrimaryInverted4};
    }
    &:disabled {
        background-color: ${Variables.ThemeLight.PrimaryInverted9};
        color: ${Variables.Colors.white};
    }
`;
export const AskButtonWrapper = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    border-radius: 20px;
    width: 120px;
    height: 60px;
    overflow: visible;

    & > * {
        position: relative;
        z-index: 1;
    }

    &::before {
        content: '';
        position: absolute;
        inset: -2px;
        border-radius: inherit;
        opacity: 0;
        filter: blur(16px);
        background: radial-gradient(
            circle at center,
            ${Variables.ThemeLight.SuccessInverted9},
            ${Variables.ThemeLight.PrimaryInverted9},
            ${Variables.ThemeLight.WarningInverted9},
            ${Variables.ThemeLight.AttentionInverted9}
        );
        pointer-events: none;
        transition: opacity 0.2s ease;
        z-index: 0;
    }

    &:hover::before {
        opacity: 0.5;
    }

    &:active::before {
        opacity: 0.6;
    }
`;
